import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Trash2,
  Copy,
  ChevronUp,
  ChevronDown,
  Save,
  Eye,
  Loader2,
  Check,
  X,
  Upload,
  Link2,
} from "lucide-react";
import { quizImageUrl } from "@/lib/quizImage";

const optionColors = ["bg-brand-blue", "bg-brand-purple", "bg-brand-green", "bg-brand-yellow"];
const newId = () => "q_" + Math.random().toString(36).slice(2, 10);

const blankQuestion = () => ({
  id: newId(),
  question: "",
  type: "multiple_choice",
  options: ["", "", "", ""],
  correct_answers: [0],
  explanation: "",
  difficulty: "medium",
  time_limit: 20,
  points: 1000,
});

export default function QuizEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(null);
  const [imgUrl, setImgUrl] = useState({});
  const [quiz, setQuiz] = useState({
    title: "",
    subject: "",
    grade: "",
    turma: "",
    description: "",
    questions: [blankQuestion()],
    status: "draft",
  });

  useEffect(() => {
    if (id) {
      api
        .get(`/quizzes/${id}`)
        .then((r) => setQuiz(r.data))
        .catch(() => toast.error("Quiz não encontrado."))
        .finally(() => setLoading(false));
    }
  }, [id]);

  const updateQ = (idx, patch) => {
    const qs = [...quiz.questions];
    qs[idx] = { ...qs[idx], ...patch };
    setQuiz({ ...quiz, questions: qs });
  };

  const setOption = (qi, oi, val) => {
    const qs = [...quiz.questions];
    const opts = [...qs[qi].options];
    opts[oi] = val;
    qs[qi] = { ...qs[qi], options: opts };
    setQuiz({ ...quiz, questions: qs });
  };

  const toggleCorrect = (qi, oi) => {
    const q = quiz.questions[qi];
    let correct;
    if (q.type === "multiple_answers") {
      correct = q.correct_answers.includes(oi)
        ? q.correct_answers.filter((x) => x !== oi)
        : [...q.correct_answers, oi];
    } else {
      correct = [oi];
    }
    updateQ(qi, { correct_answers: correct });
  };

  const addOption = (qi) => {
    const q = quiz.questions[qi];
    if (q.options.length >= 6) return;
    updateQ(qi, { options: [...q.options, ""] });
  };

  const removeOption = (qi, oi) => {
    const q = quiz.questions[qi];
    if (q.options.length <= 2) return;
    const opts = q.options.filter((_, i) => i !== oi);
    const correct = q.correct_answers
      .filter((x) => x !== oi)
      .map((x) => (x > oi ? x - 1 : x));
    updateQ(qi, { options: opts, correct_answers: correct.length ? correct : [0] });
  };

  const changeType = (qi, type) => {
    if (type === "true_false") {
      updateQ(qi, { type, options: ["Verdadeiro", "Falso"], correct_answers: [0] });
    } else {
      const q = quiz.questions[qi];
      const opts = q.options.length >= 4 ? q.options : ["", "", "", ""];
      updateQ(qi, { type, options: opts, correct_answers: [0] });
    }
  };

  const move = (qi, dir) => {
    const qs = [...quiz.questions];
    const ni = qi + dir;
    if (ni < 0 || ni >= qs.length) return;
    [qs[qi], qs[ni]] = [qs[ni], qs[qi]];
    setQuiz({ ...quiz, questions: qs });
  };

  const duplicate = (qi) => {
    const qs = [...quiz.questions];
    qs.splice(qi + 1, 0, { ...JSON.parse(JSON.stringify(qs[qi])), id: newId() });
    setQuiz({ ...quiz, questions: qs });
  };

  const remove = (qi) => {
    if (quiz.questions.length <= 1) return;
    setQuiz({ ...quiz, questions: quiz.questions.filter((_, i) => i !== qi) });
  };

  const addQuestion = () => {
    setQuiz({ ...quiz, questions: [...quiz.questions, blankQuestion()] });
  };

  const uploadImage = async (qi, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("A imagem excede 8 MB.");
      return;
    }
    setUploadingImg(qi);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/quizzes/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateQ(qi, { image: res.data.image });
      toast.success("Figura adicionada!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar a imagem.");
    } finally {
      setUploadingImg(null);
    }
  };

  const setImageUrl = (qi) => {
    const url = (imgUrl[qi] || "").trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      toast.error("Informe um link http(s) válido.");
      return;
    }
    updateQ(qi, { image: url });
    setImgUrl({ ...imgUrl, [qi]: "" });
    toast.success("Figura adicionada!");
  };

  const validate = () => {
    if (!quiz.title.trim()) return "Dê um título ao quiz.";
    for (let i = 0; i < quiz.questions.length; i++) {
      const q = quiz.questions[i];
      if (!q.question.trim()) return `Preencha o texto da questão ${i + 1}.`;
      if (q.options.some((o) => !o.trim())) return `Preencha todas as alternativas da questão ${i + 1}.`;
      if (!q.correct_answers.length) return `Marque a resposta correta da questão ${i + 1}.`;
    }
    return null;
  };

  const save = async (publish = false) => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    const payload = { ...quiz, status: publish ? "published" : "draft" };
    try {
      if (id) {
        await api.put(`/quizzes/${id}`, payload);
      } else {
        const res = await api.post("/quizzes", payload);
        navigate(`/quiz/${res.data.quiz_id}/editar`, { replace: true });
      }
      toast.success(publish ? "Quiz publicado!" : "Rascunho salvo!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="w-7 h-7 animate-spin text-brand-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="font-heading text-2xl sm:text-3xl font-black text-brand-ink">
          {id ? "Editar quiz" : "Novo quiz"}
        </h1>
        <div className="flex gap-2">
          {id && (
            <Link to={`/quiz/${id}/visualizar`}>
              <Button variant="outline" className="rounded-full gap-2" data-testid="preview-btn">
                <Eye className="w-4 h-4" /> Pré-visualizar
              </Button>
            </Link>
          )}
          <Button variant="outline" onClick={() => save(false)} disabled={saving} className="rounded-full gap-2" data-testid="save-draft-btn">
            <Save className="w-4 h-4" /> Salvar
          </Button>
          <Button onClick={() => save(true)} disabled={saving} className="rounded-full gap-2 bg-brand-green hover:bg-emerald-600" data-testid="publish-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Publicar
          </Button>
        </div>
      </div>

      {/* Meta */}
      <div className="mt-6 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm grid sm:grid-cols-3 gap-4">
        <div className="sm:col-span-3">
          <Label>Título do quiz</Label>
          <Input value={quiz.title} onChange={(e) => setQuiz({ ...quiz, title: e.target.value })} placeholder="Ex.: Fotossíntese" className="mt-1.5 rounded-xl h-11" data-testid="quiz-title" />
        </div>
        <div>
          <Label>Disciplina</Label>
          <Input value={quiz.subject} onChange={(e) => setQuiz({ ...quiz, subject: e.target.value })} className="mt-1.5 rounded-xl h-11" data-testid="quiz-subject" />
        </div>
        <div>
          <Label>Ano escolar</Label>
          <Input value={quiz.grade} onChange={(e) => setQuiz({ ...quiz, grade: e.target.value })} className="mt-1.5 rounded-xl h-11" data-testid="quiz-grade" />
        </div>
        <div>
          <Label>Turma</Label>
          <Input value={quiz.turma || ""} onChange={(e) => setQuiz({ ...quiz, turma: e.target.value })} placeholder="Ex.: 9º Ano B" className="mt-1.5 rounded-xl h-11" data-testid="quiz-turma" />
        </div>
        <div>
          <Label>Descrição</Label>
          <Input value={quiz.description} onChange={(e) => setQuiz({ ...quiz, description: e.target.value })} className="mt-1.5 rounded-xl h-11" data-testid="quiz-description" />
        </div>
      </div>

      {/* Questions */}
      <div className="mt-6 space-y-5">
        {quiz.questions.map((q, qi) => (
          <div key={q.id} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid={`question-card-${qi}`}>
            <div className="flex items-center justify-between mb-4">
              <span className="font-heading font-bold text-brand-ink">Questão {qi + 1}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => move(qi, -1)} className="p-2 rounded-lg hover:bg-slate-100 text-brand-muted" title="Subir"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => move(qi, 1)} className="p-2 rounded-lg hover:bg-slate-100 text-brand-muted" title="Descer"><ChevronDown className="w-4 h-4" /></button>
                <button onClick={() => duplicate(qi)} className="p-2 rounded-lg hover:bg-slate-100 text-brand-muted" title="Duplicar" data-testid={`dup-${qi}`}><Copy className="w-4 h-4" /></button>
                <button onClick={() => remove(qi)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Excluir" data-testid={`del-${qi}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>

            <Textarea value={q.question} onChange={(e) => updateQ(qi, { question: e.target.value })} placeholder="Digite a pergunta..." className="rounded-xl" data-testid={`q-text-${qi}`} />

            {/* Figura da pergunta */}
            <div className="mt-4">
              <Label className="text-xs">Figura da pergunta (opcional)</Label>
              {q.image ? (
                <div className="mt-2 relative inline-block">
                  <img
                    src={quizImageUrl(q.image)}
                    alt="Figura da questão"
                    className="max-h-52 rounded-xl border border-slate-200 object-contain bg-slate-50"
                    data-testid={`q-image-${qi}`}
                  />
                  <button
                    onClick={() => updateQ(qi, { image: null })}
                    className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center shadow"
                    title="Remover figura"
                    data-testid={`remove-image-${qi}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex flex-col sm:flex-row gap-2">
                  <label
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-slate-200 bg-white text-sm font-medium text-brand-ink cursor-pointer hover:bg-slate-50 shrink-0"
                    data-testid={`upload-image-${qi}`}
                  >
                    {uploadingImg === qi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Enviar imagem
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => {
                        uploadImage(qi, e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  <div className="flex-1 flex gap-2">
                    <div className="relative flex-1">
                      <Link2 className="w-4 h-4 text-brand-muted absolute left-3 top-1/2 -translate-y-1/2" />
                      <Input
                        value={imgUrl[qi] || ""}
                        onChange={(e) => setImgUrl({ ...imgUrl, [qi]: e.target.value })}
                        placeholder="ou cole o link de uma imagem..."
                        className="h-10 pl-9 rounded-xl"
                        data-testid={`image-url-${qi}`}
                      />
                    </div>
                    <Button type="button" variant="outline" onClick={() => setImageUrl(qi)} className="rounded-xl h-10 shrink-0" data-testid={`use-image-url-${qi}`}>
                      Usar link
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={q.type} onValueChange={(v) => changeType(qi, v)}>
                  <SelectTrigger className="mt-1 rounded-xl h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="multiple_choice">Múltipla escolha</SelectItem>
                    <SelectItem value="true_false">Verdadeiro ou falso</SelectItem>
                    <SelectItem value="multiple_answers">Múltiplas corretas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tempo (s)</Label>
                <Select value={String(q.time_limit)} onValueChange={(v) => updateQ(qi, { time_limit: Number(v) })}>
                  <SelectTrigger className="mt-1 rounded-xl h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>{[5, 10, 20, 30, 60, 90, 120].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Pontuação</Label>
                <Input type="number" value={q.points} onChange={(e) => updateQ(qi, { points: Number(e.target.value) })} className="mt-1 rounded-xl h-10" />
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <Label className="text-xs">Alternativas (clique no círculo para marcar a correta)</Label>
              {q.options.map((opt, oi) => {
                const correct = q.correct_answers.includes(oi);
                return (
                  <div key={oi} className="flex items-center gap-2">
                    <button
                      onClick={() => toggleCorrect(qi, oi)}
                      data-testid={`correct-${qi}-${oi}`}
                      className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-white transition-colors ${
                        correct ? "bg-brand-green" : "bg-slate-200 hover:bg-slate-300"
                      }`}
                      title="Marcar correta"
                    >
                      {correct ? <Check className="w-4 h-4" /> : String.fromCharCode(65 + oi)}
                    </button>
                    <div className={`w-2 h-8 rounded-full ${optionColors[oi % 4]}`} />
                    <Input
                      value={opt}
                      onChange={(e) => setOption(qi, oi, e.target.value)}
                      placeholder={`Alternativa ${String.fromCharCode(65 + oi)}`}
                      className="rounded-xl h-10"
                      disabled={q.type === "true_false"}
                      data-testid={`opt-${qi}-${oi}`}
                    />
                    {q.type !== "true_false" && q.options.length > 2 && (
                      <button onClick={() => removeOption(qi, oi)} className="p-2 text-slate-400 hover:text-red-500">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
              {q.type !== "true_false" && q.options.length < 6 && (
                <button onClick={() => addOption(qi)} className="text-sm text-brand-blue font-medium flex items-center gap-1 mt-1">
                  <Plus className="w-4 h-4" /> Adicionar alternativa
                </button>
              )}
            </div>

            <div className="mt-4">
              <Label className="text-xs">Explicação (opcional)</Label>
              <Textarea value={q.explanation} onChange={(e) => updateQ(qi, { explanation: e.target.value })} placeholder="Explique por que a resposta está correta..." className="mt-1 rounded-xl min-h-[60px]" />
            </div>
          </div>
        ))}
      </div>

      <Button onClick={addQuestion} variant="outline" className="mt-5 w-full rounded-2xl h-14 border-2 border-dashed gap-2 text-brand-blue hover:bg-blue-50" data-testid="add-question-btn">
        <Plus className="w-5 h-5" /> Adicionar questão
      </Button>
    </DashboardLayout>
  );
}
